import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import EntityHeader from "./common/EntityHeader";
import FloatingInput from "./common/FloatingInput";
import {
  getCensusPayors,
  censusGroupPlanAction,
  censusClientPayors,
} from "./services/soar";

function CensusPayorEntity({ client, clientProgram, showExpired }) {
  const [isOpen, setIsOpen] = useState(false);
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";
  const [censusPayor, setCensusPayor] = useState({
    id: "",
    name: "",
    type: "Census",
    clientId: client.id,
    clientName: client.name,
    payorId: "",
    groupPlanId: "",
    effective: "",
    customInsuranceId1: "",
    customInsuranceId2: "",
    notes: [],
  });
  const [clientCensusPayors, setClientCensusPayors] = useState([]);
  const [payorSearchQuery, setPayorSearchQuery] = useState("");
  const [payors, setPayors] = useState([]);
  const [groupPlans, setGroupPlans] = useState([]);
  const modules = JSON.parse(localStorage.getItem("safyInfo")).user.modules;

  useEffect(() => {
    loadCensusPayors();
    getClientCensusPayors();
  }, []);

  useEffect(() => {
    if (censusPayor.payorId) getGroupPlans();
  }, [censusPayor.payorId]);

  const loadCensusPayors = async () => {
    const response = await getCensusPayors();
    setPayors(response);
  };

  const getClientCensusPayors = async () => {
    const response = await censusClientPayors({
      command: "query",
      application: applicationId,
      keyword: "byclient",
      searchTerms: { clientId: client.id },
    });
    if (response.code === "success") {
      setClientCensusPayors(response.data.filter((p) => p.active === true));
    } else {
      toast.error("Error loading Client Census Payors");
    }
  };

  const getGroupPlans = async () => {
    const response = await censusGroupPlanAction({
      command: "query",
      application: applicationId,
      keyword: "bypayor",
      ids: [censusPayor.payorId],
    });
    if (response.code === "success") {
      setGroupPlans(response.data.filter((p) => p.active === true));
    } else {
      toast.error("Error loading Group Plans");
    }
  };

  const addNew = () => {
    clearPayor();
    setIsOpen(true);
  };

  const clearPayor = () => {
    setCensusPayor({
      id: "",
      name: "",
      type: "Census",
      clientId: client.id,
      clientName: client.name,
      payorId: "",
      groupPlanId: "",
      effective: "",
      customInsuranceId1: "",
      customInsuranceId2: "",
      notes: [],
    });
  };

  const handleSave = async () => {
    const obj = { ...censusPayor };
    obj.command = censusPayor.id ? "update" : "insert";
    obj.application = applicationId;
    obj.name = `${client.name} - ${
      payors.find((p) => p.id === censusPayor.payorId)?.name
    } - ${groupPlans.find((p) => p.id === censusPayor.groupPlanId)?.name}`;

    const response = await censusClientPayors(obj);
    if (response.code === "success") {
      toast.success("Census Client Payor saved successfully");
      getClientCensusPayors();
      setIsOpen(false);
    } else {
      toast.error("Error saving Census Client Payor");
    }
  };

  const handleExpire = async () => {
    const obj = {
      command: "expire",
      application: applicationId,
      id: censusPayor.id,
      expiration: censusPayor.expiration,
    };
    const response = await censusClientPayors(obj);
    if (response.code === "success") {
      toast.success("Census Client Payor expired successfully");
      getClientCensusPayors();
      setIsOpen(false);
    } else {
      toast.error("Error expiring Census Client Payor");
    }
  };

  return process.env.REACT_APP_ENVIRONMENT !== "Production" &&
    modules.includes("Admin") ? (
    <div>
      <Modal show={isOpen} onHide={() => setIsOpen(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Census Payor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!censusPayor.id && (
            <FloatingInput
              name="PayorSearch"
              label="Search for Payor"
              placeholder="Search for Payor..."
              value={payorSearchQuery}
              onChange={(e) => {
                setPayorSearchQuery(e.target.value);
              }}
            />
          )}
          {payors.length > 0 && (
            <>
              <FloatingInput
                type="select"
                name="payor"
                label="Payor"
                value={censusPayor.payorId}
                options={payors
                  .filter((payor) =>
                    payor.name
                      .toLowerCase()
                      .includes(payorSearchQuery.toLowerCase())
                  )
                  ?.map((payor) => ({
                    _id: payor.id,
                    name: payor.name,
                  }))}
                onChange={(selectedPayor) => {
                  setCensusPayor({
                    ...censusPayor,
                    payorId: selectedPayor.target.value,
                  });
                  setPayorSearchQuery("");
                }}
                disabled={!!censusPayor.id}
              />
              <FloatingInput
                type="select"
                name="groupPlan"
                label="Group Plan"
                value={censusPayor.groupPlanId}
                options={groupPlans.map((groupPlan) => ({
                  _id: groupPlan.id,
                  name: groupPlan.name,
                }))}
                onChange={(e) => {
                  setCensusPayor({
                    ...censusPayor,
                    groupPlanId: e.target.value,
                  });
                  setPayorSearchQuery("");
                }}
                disabled={!!censusPayor.id}
              />
              <FloatingInput
                name="id1"
                label="ID 1"
                value={censusPayor.customInsuranceId1}
                data={payors}
                onChange={(e) => {
                  setCensusPayor({
                    ...censusPayor,
                    customInsuranceId1: e.target.value,
                  });
                  setPayorSearchQuery("");
                }}
              />
              <FloatingInput
                name="id2"
                label="ID 2"
                value={censusPayor.customInsuranceId2}
                data={payors}
                onChange={(e) => {
                  setCensusPayor({
                    ...censusPayor,
                    customInsuranceId2: e.target.value,
                  });
                  setPayorSearchQuery("");
                }}
              />
              <FloatingInput
                type="date"
                name="effective"
                label="Effective Date"
                value={censusPayor.effective}
                onChange={({ currentTarget: input }) => {
                  setCensusPayor({
                    ...censusPayor,
                    effective: input.value,
                  });
                }}
              />
              {censusPayor.id && (
                <div className="mt4 bg-danger p-1 pe-3">
                  <FloatingInput
                    type="date"
                    name="expiration"
                    label="Expiration Date"
                    value={censusPayor.expiration}
                    onChange={({ currentTarget: input }) => {
                      setCensusPayor({
                        ...censusPayor,
                        expiration: input.value,
                      });
                    }}
                  />
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="btn btn-danger" onClick={() => setIsOpen(false)}>
            Close
          </div>
          {(!censusPayor.expiration || censusPayor.expiration === "") && (
            <div className="btn btn-success" onClick={handleSave}>
              Save
            </div>
          )}
          {censusPayor.expiration && (
            <div className="btn btn-primary" onClick={handleExpire}>
              Expire
            </div>
          )}
        </Modal.Footer>
      </Modal>
      <EntityHeader
        title="Client Census Payors"
        add={addNew}
        clientId={client.id}
        body={clientCensusPayors.map((ccp) => (
          <div
            className={
              ccp.expiration !== undefined && !showExpired
                ? "d-none p-2"
                : "d-block p-2"
            }
            key={ccp.id}
          >
            <Card className="card-safy">
              <Card.Header className="bg-safy1-1 text-white p-1 ps-4">
                <div className="row">
                  <div className="col-10">{ccp.name}</div>
                  <div className="col-2">
                    {(!ccp.expiration || ccp.expiration === "") && (
                      <i
                        className="fas fa-pencil float-end mt-1 me-1"
                        onClick={() => {
                          setCensusPayor(ccp);
                          setIsOpen(true);
                        }}
                      ></i>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body
                className={ccp.expiration ? "bg-warning" : "card-body-safy p-2"}
              >
                <div className="row">
                  <div className="col-3">Payor</div>
                  <div className="col-9">{ccp.payorName}</div>
                </div>
                <div className="row">
                  <div className="col-3">Group Plan</div>
                  <div className="col-9">{ccp.groupPlanName}</div>
                </div>
                <div className="row">
                  <div className="col-3">ID 1</div>
                  <div className="col-9">{ccp.customInsuranceId1}</div>
                </div>
                <div className="row">
                  <div className="col-3">ID 2</div>
                  <div className="col-9">{ccp.customInsuranceId2}</div>
                </div>
                <div className="row">
                  <div className="col-3">Effective Date</div>
                  <div className="col-9">{ccp.effective}</div>
                </div>
                {
                  <div className="row">
                    <div className="col-3">Expiration Date</div>
                    <div className="col-9">{ccp.expiration}</div>
                  </div>
                }
              </Card.Body>
            </Card>
          </div>
        ))}
      />
    </div>
  ) : null;
}

export default CensusPayorEntity;
